/**
 * The action register and the NCR/CAR detail.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md section 4.2 screen 6. The detail carries
 * the record's life: approval and issue, the disposition rounds with the USE_AS_IS
 * concession gate (a refusal shows the server's own text), the coded root cause at the
 * approval, implementation evidence, the verify-closure step that schedules the
 * effectiveness review, and the review's answer.
 *
 * Who may press what is the server's business. The screen offers the controls the
 * record's state makes meaningful and shows the refusal when the server refuses.
 */

import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { ApiError } from "../api/client";
import {
  decideContent,
  decideDisposition,
  declareImplementation,
  getAction,
  issue,
  listActions,
  proposeDisposition,
  recordEffectiveness,
  verifyClosure,
  type ActionDetail,
  type ActionSummary,
} from "../api/actions";
import { BarChart } from "../charts/BarChart";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import { HistoryButton } from "../components/HistoryDrawer";
import { ActionButton } from "@/components/ui/action-button";
import { ActionDialog } from "@/components/ui/action-dialog";
import { Button } from "@/components/ui/button";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { Toast } from "@/components/ui/toast";
import {
  AbsencePanel,
  Breadcrumbs,
  EmptyState,
  Facts,
  RefusalBox,
  Section,
  SkeletonFacts,
  SkeletonRows,
  StateChip,
  day,
  filterSet,
  stamp,
} from "./parts";
import { useQuery } from "./useQuery";
import { ServerErrorScope, ServerField } from "../lib/serverErrors";
import { mayActAs, useViewerSide } from "../lib/actor";
import { ActionDialog as HouseDialog } from "../nav/ActionDialog";

/**
 * The action register's own lifecycle (the vocabulary's "action_request_status"
 * tokens). The State dropdown lists THESE and not the global workflow vocabulary,
 * most of whose states no action request can ever carry.
 */
export const ACTION_REGISTER_STATES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "ISSUED",
  "RESPONSE_SUBMITTED",
  // Wave INST ruling 4: a disposition waiting on the engineering authority.
  "PENDING_AUTHORITY",
  "AGREED",
  "IMPLEMENTED",
  "EFFECTIVENESS_REVIEW",
  "CLOSED",
  "REJECTED",
  "CANCELLED",
] as const;

/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The action register columns: what each cell says in plain words. */
function actionRegisterColumns(): RegisterColumn<ActionSummary>[] {
  const copy = strings.actions;
  return [
    {
      id: "action",
      label: copy.columnAction,
      text: (row) => `${row.humanId} ${row.title}`,
      sortValue: (row) => row.humanId,
      className: "min-w-[14rem]",
      render: (row) => (
        <>
          <Link
            to={`/actions/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[34rem] [overflow-wrap:anywhere] text-word">{row.title}</div>
        </>
      ),
    },
    {
      id: "type",
      label: copy.columnType,
      text: (row) => term(row.actionType),
      filter: true,
    },
    {
      id: "contractor",
      label: copy.columnContractor,
      text: (row) => row.contractorName ?? "",
      sortValue: (row) => row.contractorName ?? "",
    },
    {
      id: "round",
      label: copy.columnRound,
      text: (row) => String(row.roundNo),
      sortValue: (row) => row.roundNo,
    },
    {
      id: "state",
      label: copy.columnState,
      text: (row) => term(row.actionStatus),
      filter: true,
      render: (row) => (
        <>
          <StateChip state={row.actionStatus} />
          {row.effectivenessReviewDate !== null && (
            <div className="text-xs text-muted-foreground" data-testid="register-review-date">
              {copy.effectivenessReviewDue(day(row.effectivenessReviewDate))}
            </div>
          )}
        </>
      ),
    },
  ];
}

export function ActionsPage(): React.JSX.Element {
  const token = useToken();
  const [parameters, setParameters] = useSearchParams();
  const typeParam = parameters.get("type");
  const stateParam = parameters.get("state");
  const typeSet = filterSet(typeParam);
  const stateSet = filterSet(stateParam);
  const stage = parameters.get("stage");
  const discipline = parameters.get("discipline");

  const register = useQuery(
    () =>
      listActions(token, {
        actionType: typeSet.length === 1 ? (typeSet[0] ?? null) : null,
        actionStatus: stateSet.length === 1 ? (stateSet[0] ?? null) : null,
        stageId: stage,
        disciplineId: discipline,
      }),
    [token, typeParam, stateParam, stage, discipline],
  );

  const setParameter = (name: string, value: string): void => {
    const next = new URLSearchParams(parameters);
    if (value.length === 0) {
      next.delete(name);
    } else {
      next.set(name, value);
    }
    next.delete("offset");
    setParameters(next);
  };

  const columns = useMemo(() => actionRegisterColumns(), []);

  const filteredItems = useMemo(() => {
    if (register.kind !== "ready") return [];
    return register.data.items.filter((item) => {
      if (typeSet.length > 0 && !typeSet.includes(item.actionType)) {
        return false;
      }
      if (stateSet.length > 0 && !stateSet.includes(item.actionStatus)) {
        return false;
      }
      return true;
    });
  }, [register, typeSet, stateSet]);

  return (
    <>
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.actions.heading}</h2>
          <p className="q-shell__lede">{strings.actions.lede}</p>
        </div>
      </header>

      {register.kind === "ready" && (
        <BarChart
          ariaLabel={`${strings.actions.heading} · ${strings.common.status}`}
          frameClassName="q-chart__frame--lead"
          tickAngle={-35}
          data={ACTION_REGISTER_STATES.map((state) => ({
            label: term(state),
            value: register.data.items.filter((row) => row.actionStatus === state).length,
          }))}
          onSelectDatum={(datum) => {
            const token_ = ACTION_REGISTER_STATES.find((state) => term(state) === datum.label);
            if (token_ !== undefined) {
              setParameter("state", token_);
            }
          }}
        />
      )}

      <RegisterCard>
        {register.kind === "loading" && <SkeletonRows />}
        {register.kind === "absence" && <AbsencePanel />}
        {register.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {register.kind === "ready" &&
          (filteredItems.length === 0 ? (
            <EmptyState
              title={strings.actions.stateEmptyTitle}
              body={strings.actions.stateEmptyBody}
            />
          ) : (
            <DataTable
              data={filteredItems}
              columns={columns}
              caption={strings.actions.heading}
              testId="actions"
              getRowId={(row) => row.id}
            />
          ))}
      </RegisterCard>
    </>
  );
}

export function ActionDetailPage(): React.JSX.Element {
  const token = useToken();
  const { actionRequestId = "" } = useParams();
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  // Wave W1.5: each act is offered only to the side that performs it, and a person's
  // own record says so before any attempt (no-self-approval).
  const side = useViewerSide();
  const operatorActs = mayActAs(side, "operator");
  // Wave INST ruling 4: an AUTHORITY_REVIEW task lands here with act=authority.
  const [params] = useSearchParams();

  const record = useQuery(
    () => getAction(token, actionRequestId),
    [token, actionRequestId, reload],
  );

  const reread = (): void => {
    setReload((n) => n + 1);
  };

  async function run(action: () => Promise<void>): Promise<void> {
    setActionError(null);
    try {
      await action();
      // The dialog submits land in the toast bar (M5 P5e #8); refusals stay in place.
      setToast({
        message: strings.schedules.actionToastDone(strings.actions.detailHeading),
        tone: "info",
      });
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(error);
      }
    }
  }

  if (record.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.actions.detailHeading}</h2>
        <AbsencePanel />
      </>
    );
  }
  if (record.kind !== "ready") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.actions.detailHeading}</h2>
        {record.kind === "loading" && <SkeletonFacts fields={6} />}
        {record.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }

  const detail: ActionDetail = record.data;

  return (
    <ServerErrorScope error={actionError}>
      <Breadcrumbs
        trail={[{ label: strings.actions.heading, to: "/actions" }, { label: detail.humanId }]}
      />
      <h2 className="q-shell__heading">
        {detail.humanId} {term(detail.actionType)}
      </h2>
      <p className="q-shell__lede">
        <span data-testid="action-status">
          <StateChip state={detail.actionStatus} />
        </span>{" "}
        · {strings.actions.columnRound} {String(detail.roundNo)}
      </p>

      {toast !== null && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      {actionError !== null && <RefusalBox error={actionError} />}

      <div className="q-section-bands" data-testid="action-detail-sections">
        <Section heading={strings.actions.detailHeading}>
          <Facts
            entries={[
              [strings.common.humanId, <span key="id">{detail.humanId}</span>],
              [
                strings.actions.originLabel,
                detail.originFindingId !== null ? (
                  <Link key="origin" className="q-link" to={`/findings/${detail.originFindingId}`}>
                    {strings.findings.heading}
                  </Link>
                ) : (
                  <span key="origin">{strings.actions.noOrigin}</span>
                ),
              ],
              [
                strings.actions.columnContractor,
                <span key="party">{detail.contractorName ?? ""}</span>,
              ],
              [
                strings.actions.targetDateLabel,
                <span key="target">{day(detail.targetImplementationDate)}</span>,
              ],
            ]}
          />
          <p className="q-thread__body q-gap-top">{detail.description}</p>

          <div className="q-actions-row q-gap-top">
            <HistoryButton
              objectType="ACTION_REQUEST"
              objectId={detail.id}
              recordLabel={detail.humanId}
            />
            {detail.actionStatus === "SUBMITTED" && operatorActs && (
              <>
                <ActionButton
                  variant="outline"
                  data-testid="approve-content"
                  disabledReason={detail.submittedByMe ? strings.actions.selfApprovalBlocked : null}
                  reasonTestId="self-approval-blocked"
                  onClick={() => {
                    void run(async () => {
                      await decideContent(token, detail.id, "approve", null);
                    });
                  }}
                >
                  {strings.actions.approveDisposition}
                </ActionButton>
                <RejectContentButton
                  onReject={(comment) => {
                    void run(async () => {
                      await decideContent(token, detail.id, "reject", comment);
                    });
                  }}
                />
              </>
            )}
            {detail.actionStatus === "APPROVED" && operatorActs && (
              <ActionButton
                variant="outline"
                data-testid="issue-action"
                disabledReason={detail.submittedByMe ? strings.actions.selfApprovalBlocked : null}
                reasonTestId="self-approval-blocked"
                onClick={() => {
                  void run(async () => {
                    await issue(token, detail.id);
                  });
                }}
              >
                {strings.actions.issueLabel}
              </ActionButton>
            )}
          </div>
        </Section>

        <DispositionSection
          detail={detail}
          onAct={run}
          openAuthority={params.get("act") === "authority"}
          onDone={(message) => {
            setToast({ message, tone: "info" });
            reread();
          }}
        />
        {(detail.actionStatus === "AGREED" || detail.evidence.length > 0) && (
          <ImplementationSection detail={detail} onAct={run} />
        )}
        {detail.actionStatus === "IMPLEMENTED" && (
          <VerifyClosureSection detail={detail} onAct={run} />
        )}
        {detail.actionStatus === "EFFECTIVENESS_REVIEW" && (
          <EffectivenessSection detail={detail} onAct={run} />
        )}
        {detail.actionStatus === "CLOSED" && (
          <Section heading={strings.actions.effectivenessHeading}>
            <p className="q-note">
              {detail.effectivenessOutcome !== null
                ? `${term(detail.effectivenessOutcome)} · ${detail.effectivenessNotes ?? ""}`
                : strings.common.empty}
            </p>
          </Section>
        )}

        <Section heading={strings.actions.historyHeading}>
          <div className="q-thread">
            {detail.transitions.map((entry) => (
              <article className="q-thread__entry" key={entry.id}>
                <p className="q-thread__meta">
                  <strong>{term(entry.event)}</strong> · {term(entry.fromState)} →{" "}
                  {term(entry.toState)} · {stamp(entry.at)}
                </p>
                {entry.comment !== null && <p className="q-thread__body">{entry.comment}</p>}
              </article>
            ))}
          </div>
        </Section>
      </div>
    </ServerErrorScope>
  );
}

function RejectContentButton({
  onReject,
}: {
  onReject: (comment: string) => void;
}): React.JSX.Element {
  const [comment, setComment] = useState("");
  return (
    <>
      <ServerField field="comment">
        <Input
          aria-label={strings.actions.rejectCommentLabel}
          value={comment}
          onChange={(event) => {
            setComment(event.target.value);
          }}
        />
      </ServerField>
      <Button
        variant="outline"
        disabled={comment.length === 0}
        onClick={() => {
          onReject(comment);
        }}
      >
        {strings.findings.rejectResponse}
      </Button>
    </>
  );
}

function DispositionSection({
  detail,
  onAct,
  openAuthority = false,
  onDone,
}: {
  detail: ActionDetail;
  onAct: (action: () => Promise<void>) => Promise<void>;
  /** Wave INST: opened from an AUTHORITY_REVIEW task, the authority's dialog opens. */
  openAuthority?: boolean;
  /** A dialog that keeps its own refusal reports its success here. */
  onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [dispositionType, setDispositionType] = useState("");
  const [justification, setJustification] = useState("");
  const [concession, setConcession] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [plan, setPlan] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [returnComment, setReturnComment] = useState("");
  // The five disposition/implementation forms open in the ActionDialog
  // (M5 P6 seat W #1); each section carries only its compact opener row.
  const [dialog, setDialog] = useState<"propose" | "decide" | null>(null);
  // Wave INST ruling 4: the authority's second hand and the return path, each a dialog
  // that keeps the server's refusal inside it.
  const [authorityDialog, setAuthorityDialog] = useState<"complete" | "return" | null>(null);
  const [authorityDismissed, setAuthorityDismissed] = useState(false);

  const side = useViewerSide();
  const pendingAuthority = detail.actionStatus === "PENDING_AUTHORITY";
  const completesAuthority =
    pendingAuthority && detail.mayCompleteAuthority && !detail.authorityProposedByMe;
  const shownAuthorityDialog =
    authorityDialog ??
    (openAuthority && completesAuthority && !authorityDismissed ? "complete" : null);
  // Wave W1.5: the contractor proposes, the operator decides, never the proposer.
  const proposing = detail.actionStatus === "ISSUED" && mayActAs(side, "contractor");
  const deciding = detail.actionStatus === "RESPONSE_SUBMITTED" && mayActAs(side, "operator");

  return (
    <Section heading={strings.actions.dispositionHeading}>
      {detail.dispositionType !== null && (
        <Facts
          entries={[
            [
              strings.actions.currentDisposition,
              <span key="disposition">{term(detail.dispositionType)}</span>,
            ],
            [
              strings.schedules.justificationLabel,
              <span key="justification">{detail.dispositionJustification ?? ""}</span>,
            ],
            [
              strings.actions.concessionLabel,
              <span key="concession">{detail.concessionReference ?? ""}</span>,
            ],
            [
              strings.actions.rootCauseCategoryLabel,
              <span key="cause">{term(detail.rootCauseCategory ?? "")}</span>,
            ],
          ]}
        />
      )}

      {proposing && (
        <div className="q-actions-row" data-testid="disposition-actions">
          <Button
            variant="outline"
            data-testid="propose-disposition-open"
            onClick={() => {
              setDialog("propose");
            }}
          >
            {strings.actions.proposeDisposition}
          </Button>
        </div>
      )}

      {pendingAuthority && (
        <div className="q-subform" data-testid="disposition-pending-authority">
          <p>
            <StateChip state={detail.actionStatus} />{" "}
            {detail.authorityProposedByMe
              ? strings.actions.authorityPendingMine
              : strings.actions.authorityPendingOther}
          </p>
          {detail.dispositionAuthorityProposedAt !== null && (
            <p className="q-note">
              {strings.actions.authorityProposedOn(stamp(detail.dispositionAuthorityProposedAt))}
            </p>
          )}
          {(completesAuthority || mayActAs(side, "operator")) && (
            <div className="q-actions-row" data-testid="authority-actions">
              {completesAuthority && (
                <Button
                  data-testid="authority-complete-open"
                  onClick={() => {
                    setAuthorityDialog("complete");
                  }}
                >
                  {strings.actions.authorityComplete}
                </Button>
              )}
              {mayActAs(side, "operator") && (
                <Button
                  variant="outline"
                  data-testid="authority-return-open"
                  onClick={() => {
                    setAuthorityDialog("return");
                  }}
                >
                  {strings.actions.authorityReturn}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {shownAuthorityDialog !== null && (
        <AuthorityDispositionDialog
          detail={detail}
          mode={shownAuthorityDialog}
          onClose={() => {
            setAuthorityDialog(null);
            setAuthorityDismissed(true);
          }}
          onDone={(message) => {
            setAuthorityDialog(null);
            setAuthorityDismissed(true);
            onDone(message);
          }}
        />
      )}

      {deciding && (
        <div className="q-actions-row" data-testid="decision-actions">
          <ActionButton
            variant="outline"
            data-testid="decide-disposition-open"
            disabledReason={
              detail.dispositionProposedByMe ? strings.actions.selfDispositionBlocked : null
            }
            reasonTestId="self-disposition-blocked"
            onClick={() => {
              setDialog("decide");
            }}
          >
            {detail.dispositionNeedsAuthority
              ? strings.actions.proposeToAuthority
              : strings.actions.approveDisposition}
          </ActionButton>
        </div>
      )}

      {dialog === "propose" && (
        <ActionDialog
          open
          title={strings.actions.proposeDisposition}
          onClose={() => {
            setDialog(null);
          }}
        >
          <div className="flex flex-col gap-4">
            <Field>
              <Label htmlFor="disposition-type">{strings.actions.dispositionTypeLabel}</Label>
              <ServerField field="disposition_type">
                <NativeSelect
                  id="disposition-type"
                  data-testid="disposition-type"
                  value={dispositionType}
                  onChange={(event) => {
                    setDispositionType(event.target.value);
                  }}
                >
                  <option value="">{strings.actions.dispositionTypePlaceholder}</option>
                  {Object.entries(strings.terms.dispositionTypes).map(([token_, label]) => (
                    <option key={token_} value={token_}>
                      {label}
                    </option>
                  ))}
                </NativeSelect>
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="disposition-justification">
                {strings.schedules.justificationLabel}
              </Label>
              <ServerField field="disposition_justification">
                <Textarea
                  id="disposition-justification"
                  value={justification}
                  onChange={(event) => {
                    setJustification(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            {dispositionType === "USE_AS_IS" && (
              <Field>
                <Label htmlFor="disposition-concession">{strings.actions.concessionLabel}</Label>
                <ServerField field="concession_reference">
                  <Input
                    id="disposition-concession"
                    value={concession}
                    onChange={(event) => {
                      setConcession(event.target.value);
                    }}
                  />
                </ServerField>
                <p className="text-sm text-muted-foreground">{strings.actions.concessionHelper}</p>
              </Field>
            )}
            <Field>
              <Label htmlFor="disposition-analysis">{strings.actions.rootCauseAnalysisLabel}</Label>
              <ServerField field="root_cause_analysis">
                <Textarea
                  id="disposition-analysis"
                  value={analysis}
                  onChange={(event) => {
                    setAnalysis(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="disposition-target">{strings.actions.targetDateLabel}</Label>
              <ServerField field="target_implementation_date">
                <Input
                  id="disposition-target"
                  type="date"
                  value={targetDate}
                  onChange={(event) => {
                    setTargetDate(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid="propose-disposition"
              disabled={justification.trim().length === 0}
              title={
                justification.trim().length === 0
                  ? strings.actions.justificationDisabledReason
                  : undefined
              }
              onClick={() => {
                void onAct(async () => {
                  await proposeDisposition(token, detail.id, {
                    disposition_type: dispositionType,
                    disposition_justification: justification,
                    concession_reference: concession.length > 0 ? concession : null,
                    root_cause_category: rootCause.length > 0 ? rootCause : null,
                    root_cause_analysis: analysis.length > 0 ? analysis : null,
                    corrective_action_plan: plan.length > 0 ? plan : null,
                    target_implementation_date: targetDate.length > 0 ? targetDate : null,
                  });
                });
                setDialog(null);
              }}
            >
              {strings.actions.proposeDisposition}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}

      {dialog === "decide" && (
        <ActionDialog
          open
          title={strings.actions.approveDisposition}
          onClose={() => {
            setDialog(null);
          }}
        >
          {detail.dispositionNeedsAuthority && (
            <p className="q-note" data-testid="disposition-authority-note">
              {strings.actions.authorityNote}
            </p>
          )}
          <div className="flex flex-col gap-4">
            <Field>
              <Label htmlFor="root-cause">{strings.actions.rootCauseCategoryLabel}</Label>
              <ServerField field="root_cause_category">
                <NativeSelect
                  id="root-cause"
                  data-testid="root-cause-category"
                  value={rootCause}
                  onChange={(event) => {
                    setRootCause(event.target.value);
                  }}
                >
                  <option value="">{strings.actions.rootCauseCategoryPlaceholder}</option>
                  {Object.entries(strings.terms.rootCauseCategories).map(([token_, label]) => (
                    <option key={token_} value={token_}>
                      {label}
                    </option>
                  ))}
                </NativeSelect>
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="corrective-plan">{strings.actions.correctivePlanLabel}</Label>
              <ServerField field="corrective_action_plan">
                <Textarea
                  id="corrective-plan"
                  value={plan}
                  onChange={(event) => {
                    setPlan(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="return-comment">{strings.actions.rejectCommentLabel}</Label>
              <ServerField field="comment">
                <Textarea
                  id="return-comment"
                  value={returnComment}
                  onChange={(event) => {
                    setReturnComment(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              variant="outline"
              disabled={returnComment.length === 0}
              title={
                returnComment.length === 0 ? strings.actions.justificationDisabledReason : undefined
              }
              onClick={() => {
                void onAct(async () => {
                  await decideDisposition(token, detail.id, "reject", {
                    comment: returnComment,
                  });
                });
                setDialog(null);
              }}
            >
              {strings.actions.rejectDisposition}
            </Button>
            <Button
              data-testid="approve-disposition-confirm"
              onClick={() => {
                void onAct(async () => {
                  await decideDisposition(token, detail.id, "approve", {
                    root_cause_category: rootCause.length > 0 ? rootCause : null,
                    corrective_action_plan: plan.length > 0 ? plan : null,
                  });
                });
                setDialog(null);
              }}
            >
              {detail.dispositionNeedsAuthority
                ? strings.actions.proposeToAuthority
                : strings.actions.approveDisposition}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}
    </Section>
  );
}

/**
 * Wave INST ruling 4: the disposition waiting on the engineering authority. "complete" is
 * the authority's second hand on the approve route (PENDING_AUTHORITY to AGREED), with the
 * contractor's disposition and justification before them; "return" sends it back to the
 * contractor with a comment (PENDING_AUTHORITY to ISSUED). The refusal stays inside.
 */
function AuthorityDispositionDialog({
  detail,
  mode,
  onClose,
  onDone,
}: {
  readonly detail: ActionDetail;
  readonly mode: "complete" | "return";
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const returning = mode === "return";

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (returning) {
        await decideDisposition(token, detail.id, "reject", { comment: comment.trim() });
        onDone(strings.actions.authorityReturned);
      } else {
        await decideDisposition(token, detail.id, "approve", {});
        onDone(strings.actions.authorityCompleted);
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <HouseDialog
      open
      title={`${detail.humanId}: ${returning ? strings.actions.authorityReturn : strings.actions.authorityHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">
        {returning ? strings.actions.authorityReturnLede : strings.actions.authorityLede}
      </p>
      <div className="q-subform" data-testid="authority-disposition">
        <Facts
          entries={[
            [
              strings.actions.currentDisposition,
              <span key="disposition">{term(detail.dispositionType)}</span>,
            ],
            [
              strings.schedules.justificationLabel,
              <span key="justification" data-testid="authority-justification">
                {detail.dispositionJustification ?? ""}
              </span>,
            ],
            [
              strings.actions.concessionLabel,
              <span key="concession">{detail.concessionReference ?? ""}</span>,
            ],
            [
              strings.actions.authorityProposedBy,
              <span key="proposer">{detail.dispositionProposedByName ?? ""}</span>,
            ],
          ]}
        />
      </div>
      {returning && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="authority-return-comment">
            {strings.actions.rejectCommentLabel}
          </label>
          <ServerField field="comment">
            <textarea
              id="authority-return-comment"
              className="q-textarea"
              data-testid="authority-return-comment"
              aria-required="true"
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid={returning ? "authority-return-send" : "authority-complete-send"}
          disabled={busy || (returning && comment.trim() === "")}
          title={
            returning && comment.trim() === ""
              ? strings.actions.justificationDisabledReason
              : undefined
          }
          onClick={() => {
            void send();
          }}
        >
          {returning ? strings.actions.authorityReturn : strings.actions.authorityComplete}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </HouseDialog>
  );
}

function ImplementationSection({
  detail,
  onAct,
}: {
  detail: ActionDetail;
  onAct: (action: () => Promise<void>) => Promise<void>;
}): React.JSX.Element {
  const token = useToken();
  const [rows, setRows] = useState<{ evidence_text: string; file_ref: string }[]>([]);
  const [implementOpen, setImplementOpen] = useState(false);
  const side = useViewerSide();

  return (
    <Section heading={strings.actions.implementationHeading}>
      {detail.evidence.map((entry) => (
        <p className="q-note" key={entry.id}>
          {String(entry.seq)}. {entry.evidenceText} ({entry.fileRef}) · {stamp(entry.uploadedAt)}
        </p>
      ))}
      {detail.actionStatus === "AGREED" && mayActAs(side, "contractor") && (
        <>
          <div className="q-actions-row" data-testid="implementation-actions">
            <Button
              variant="outline"
              data-testid="declare-implementation-open"
              onClick={() => {
                setImplementOpen(true);
              }}
            >
              {strings.actions.declareImplementation}
            </Button>
          </div>
          {implementOpen && (
            <ActionDialog
              open
              title={strings.actions.declareImplementation}
              onClose={() => {
                setImplementOpen(false);
              }}
            >
              <div className="flex flex-col gap-4">
                {rows.map((row, index) => (
                  <div className="flex flex-wrap items-center gap-3" key={index}>
                    <ServerField field={`evidence_items.${String(index)}.evidence_text`}>
                      <Input
                        aria-label={strings.actions.evidenceTextLabel}
                        value={row.evidence_text}
                        onChange={(event) => {
                          const next = [...rows];
                          next[index] = { ...row, evidence_text: event.target.value };
                          setRows(next);
                        }}
                      />
                    </ServerField>
                    <ServerField field={`evidence_items.${String(index)}.file_ref`}>
                      <Input
                        aria-label={strings.actions.fileRefLabel}
                        value={row.file_ref}
                        onChange={(event) => {
                          const next = [...rows];
                          next[index] = { ...row, file_ref: event.target.value };
                          setRows(next);
                        }}
                      />
                    </ServerField>
                  </div>
                ))}
                <div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRows([...rows, { evidence_text: "", file_ref: "" }]);
                    }}
                  >
                    {strings.actions.addEvidence}
                  </Button>
                </div>
              </div>
              <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setImplementOpen(false);
                  }}
                >
                  {strings.common.cancel}
                </Button>
                <Button
                  data-testid="declare-implementation-confirm"
                  disabled={rows.length === 0}
                  onClick={() => {
                    void onAct(async () => {
                      await declareImplementation(token, detail.id, rows);
                    });
                    setImplementOpen(false);
                  }}
                >
                  {strings.actions.declareImplementation}
                </Button>
              </DialogFooter>
            </ActionDialog>
          )}
        </>
      )}
    </Section>
  );
}

function VerifyClosureSection({
  detail,
  onAct,
}: {
  detail: ActionDetail;
  onAct: (action: () => Promise<void>) => Promise<void>;
}): React.JSX.Element {
  const token = useToken();
  const [closureNotes, setClosureNotes] = useState("");
  // Wave R0 Break 11: the verifier's word on the effectiveness review, starting from the
  // record's stored flag and always sent.
  const [reviewRequired, setReviewRequired] = useState(detail.effectivenessReviewRequired);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const side = useViewerSide();
  if (!mayActAs(side, "operator")) {
    return <></>;
  }
  return (
    <Section heading={strings.actions.verifyClosureHeading}>
      <div className="q-actions-row" data-testid="verify-actions">
        <ActionButton
          variant="outline"
          data-testid="verify-closure-open"
          disabledReason={
            detail.dispositionProposedByMe ? strings.actions.selfDispositionBlocked : null
          }
          reasonTestId="self-closure-blocked"
          onClick={() => {
            setReviewRequired(detail.effectivenessReviewRequired);
            setVerifyOpen(true);
          }}
        >
          {strings.actions.verifyClosure}
        </ActionButton>
      </div>
      {verifyOpen && (
        <ActionDialog
          open
          title={strings.actions.verifyClosure}
          onClose={() => {
            setVerifyOpen(false);
          }}
        >
          <div className="flex flex-col gap-4">
            <Field>
              <Label htmlFor="closure-notes">{strings.actions.closureNotesLabel}</Label>
              <ServerField field="closure_notes">
                <Textarea
                  id="closure-notes"
                  value={closureNotes}
                  onChange={(event) => {
                    setClosureNotes(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <div className="flex items-start gap-3">
              <ServerField field="effectiveness_review_required">
                <input
                  id="effectiveness-review-required"
                  type="checkbox"
                  className="mt-1 size-5"
                  data-testid="effectiveness-review-required"
                  checked={reviewRequired}
                  onChange={(event) => {
                    setReviewRequired(event.target.checked);
                  }}
                />
              </ServerField>
              <div>
                <Label htmlFor="effectiveness-review-required">
                  {strings.actions.effectivenessReviewRequiredLabel}
                </Label>
                <p className="q-note">{strings.actions.effectivenessReviewRequiredHint}</p>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setVerifyOpen(false);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid="verify-closure-confirm"
              onClick={() => {
                void onAct(async () => {
                  await verifyClosure(token, detail.id, {
                    closure_notes: closureNotes.length > 0 ? closureNotes : null,
                    effectiveness_review_required: reviewRequired,
                  });
                });
                setVerifyOpen(false);
              }}
            >
              {strings.actions.verifyClosure}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}
    </Section>
  );
}

function EffectivenessSection({
  detail,
  onAct,
}: {
  detail: ActionDetail;
  onAct: (action: () => Promise<void>) => Promise<void>;
}): React.JSX.Element {
  const token = useToken();
  const [outcome, setOutcome] = useState("EFFECTIVE");
  const [notes, setNotes] = useState("");
  const [effectiveOpen, setEffectiveOpen] = useState(false);
  const side = useViewerSide();
  if (!mayActAs(side, "operator")) {
    return <></>;
  }
  return (
    <Section heading={strings.actions.effectivenessHeading}>
      {detail.effectivenessReviewDate !== null && (
        <p className="q-note" data-testid="effectiveness-date">
          {strings.actions.effectivenessScheduled(day(detail.effectivenessReviewDate))}
        </p>
      )}
      <div className="q-actions-row" data-testid="effectiveness-actions">
        <Button
          variant="outline"
          data-testid="record-effectiveness-open"
          onClick={() => {
            setEffectiveOpen(true);
          }}
        >
          {strings.actions.recordEffectiveness}
        </Button>
      </div>
      {effectiveOpen && (
        <ActionDialog
          open
          title={strings.actions.recordEffectiveness}
          onClose={() => {
            setEffectiveOpen(false);
          }}
        >
          <div className="flex flex-col gap-4">
            <Field>
              <Label htmlFor="effectiveness-outcome">
                {strings.actions.effectivenessOutcomeLabel}
              </Label>
              <ServerField field="effectiveness_outcome">
                <NativeSelect
                  id="effectiveness-outcome"
                  data-testid="effectiveness-outcome"
                  value={outcome}
                  onChange={(event) => {
                    setOutcome(event.target.value);
                  }}
                >
                  <option value="EFFECTIVE">{strings.actions.effectivenessEffective}</option>
                  <option value="NOT_EFFECTIVE">{strings.actions.effectivenessNotEffective}</option>
                </NativeSelect>
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="effectiveness-notes">{strings.actions.effectivenessNotesLabel}</Label>
              <ServerField field="effectiveness_notes">
                <Textarea
                  id="effectiveness-notes"
                  value={notes}
                  onChange={(event) => {
                    setNotes(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <p className="text-sm text-muted-foreground">{strings.actions.followUpNote}</p>
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setEffectiveOpen(false);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid="record-effectiveness-confirm"
              onClick={() => {
                void onAct(async () => {
                  await recordEffectiveness(token, detail.id, {
                    effectiveness_outcome: outcome,
                    effectiveness_notes: notes.length > 0 ? notes : null,
                  });
                });
                setEffectiveOpen(false);
              }}
            >
              {strings.actions.recordEffectiveness}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}
    </Section>
  );
}
